import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, FormHelperText, MenuItem, Select, Tab, Tabs, TextField, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Icon, SimpleModal, toast } from '@livable/saas-react-components';
import { useStore } from 'src/stores';
import { findHeader, parseCsvText } from '../submeterCsv';
import { clearPlaced, findEmptyUnitIds, findHolders, findPlaced, forgetUnitPlacements, moveMeter, normalizeMeterId, prefillMeterIds, readSheet, sortByLocation } from './sheetMeters';
const CELL_BORDER = '1px solid #e0e0e0';
const LABEL_WIDTH = 300;
const SHARED_METER_QUESTION = 'Do these units actually share the same meter? If yes, set this utility up for Ratio Utility Billing.';
const SAME_UNIT_DUPLICATE_MESSAGE = 'Already entered for this unit';
const SHARED_ROW_MESSAGE = 'Shared meter: edit in By unit';
const SAVE_REFUSED_MESSAGE = 'Meter IDs were not saved. Try again.';
const DELETE_REFUSED_MESSAGE = 'Meter IDs were not deleted. Try again.';
const NO_ISSUES = {
  duplicateSlots: [],
  sharedWarnings: []
};
const toastIfRefused = (resp, message) => {
  if (resp !== null && resp !== void 0 && resp.isSuccessful) return false;
  if (resp !== undefined) toast('error', {
    body: [message]
  });
  return true;
};
const findMeterIdIssues = (unitList, unitMeterIds) => {
  const unitsByMeterId = new Map();
  unitList.forEach(unit => {
    const ids = new Set((unitMeterIds[unit.id] || []).map(normalizeMeterId));
    ids.forEach(id => {
      unitsByMeterId.set(id, [...(unitsByMeterId.get(id) || []), unit]);
    });
  });
  const issuesByUnit = {};
  unitList.forEach(unit => {
    const ids = unitMeterIds[unit.id] || [''];
    const normalized = ids.map(normalizeMeterId);
    const duplicateSlots = normalized.map(id => !!id && normalized.filter(other => other === id).length > 1);
    const sharedWarnings = [...new Set(normalized.filter(Boolean))].filter(id => unitsByMeterId.get(id).length > 1).map(id => {
      const others = unitsByMeterId.get(id).filter(u => u.id !== unit.id).map(u => u.label);
      return "".concat(ids[normalized.indexOf(id)].trim(), " is also entered for ").concat(others.join(', '), ". ").concat(SHARED_METER_QUESTION);
    });
    issuesByUnit[unit.id] = {
      duplicateSlots,
      sharedWarnings
    };
  });
  return issuesByUnit;
};

// Module-level memoized row — only re-renders when its own props actually change
const UnitMeterIdRow = /*#__PURE__*/React.memo(_ref => {
  let unit = _ref.unit,
    idx = _ref.idx,
    unitCount = _ref.unitCount,
    ids = _ref.ids,
    csvMeterOptions = _ref.csvMeterOptions,
    filteredOptions = _ref.filteredOptions,
    manualFlags = _ref.manualFlags,
    issues = _ref.issues,
    onAdd = _ref.onAdd,
    onRemove = _ref.onRemove,
    onUpdate = _ref.onUpdate,
    onToggleManual = _ref.onToggleManual;
  return /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'flex-start',
      borderBottom: idx < unitCount - 1 ? CELL_BORDER : undefined
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: LABEL_WIDTH,
      flexShrink: 0,
      padding: '12px 12px 12px 0'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, unit.label)), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      padding: '4px 0 4px 12px',
      borderLeft: CELL_BORDER
    }
  }, ids.map((val, i) => {
    var _filteredOptions$i;
    const showSelect = csvMeterOptions.length > 0 && !manualFlags[i];
    const slotOptions = filteredOptions ? (_filteredOptions$i = filteredOptions[i]) !== null && _filteredOptions$i !== void 0 ? _filteredOptions$i : csvMeterOptions : csvMeterOptions;
    const isDuplicate = !!issues.duplicateSlots[i];
    const inputId = "meter-id-".concat(unit.id, "-").concat(i);
    return /*#__PURE__*/React.createElement("div", {
      key: i,
      style: {
        marginBottom: i < ids.length - 1 ? 4 : 0
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }
    }, showSelect ? /*#__PURE__*/React.createElement(FormControl, {
      size: "small",
      fullWidth: true,
      error: isDuplicate
    }, /*#__PURE__*/React.createElement(Select, {
      displayEmpty: true,
      value: val,
      SelectDisplayProps: isDuplicate ? {
        'aria-invalid': true,
        'aria-describedby': "".concat(inputId, "-helper-text")
      } : undefined,
      onChange: e => onUpdate(unit.id, i, e.target.value),
      renderValue: v => v ? v : /*#__PURE__*/React.createElement("span", {
        style: {
          color: '#9e9e9e'
        }
      }, "Select meter ID")
    }, slotOptions.map(opt => /*#__PURE__*/React.createElement(MenuItem, {
      key: opt.value,
      value: opt.value
    }, opt.label))), isDuplicate && /*#__PURE__*/React.createElement(FormHelperText, {
      id: "".concat(inputId, "-helper-text")
    }, SAME_UNIT_DUPLICATE_MESSAGE)) : /*#__PURE__*/React.createElement(TextField, {
      size: "small",
      fullWidth: true,
      placeholder: "123456789",
      value: val,
      onChange: e => onUpdate(unit.id, i, e.target.value),
      error: isDuplicate,
      helperText: isDuplicate ? SAME_UNIT_DUPLICATE_MESSAGE : undefined
    }), i === 0 ? /*#__PURE__*/React.createElement(Button, {
      variant: "actionButtonInvert",
      size: "small",
      style: {
        minWidth: 32,
        flexShrink: 0,
        padding: '5px 8px'
      },
      onClick: () => onAdd(unit.id)
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "plus"
    })) : /*#__PURE__*/React.createElement(Button, {
      variant: "actionButtonInvert",
      size: "small",
      style: {
        minWidth: 32,
        flexShrink: 0,
        padding: '5px 8px'
      },
      onClick: () => onRemove(unit.id, i)
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "trash"
    }))), showSelect && i > 0 && i === ids.length - 1 && /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "primary",
      style: {
        cursor: 'pointer',
        marginTop: 2,
        fontSize: 12
      },
      onClick: () => onToggleManual(unit.id, i)
    }, "Enter manually"));
  }), issues.sharedWarnings.map(warning => /*#__PURE__*/React.createElement(Typography, {
    key: warning,
    role: "status",
    variant: "body2",
    color: "light",
    style: {
      marginTop: 4,
      fontSize: 12
    }
  }, warning))));
});
const SheetMeterRow = /*#__PURE__*/React.memo(_ref2 => {
  let meter = _ref2.meter,
    holder = _ref2.holder,
    sharedLabel = _ref2.sharedLabel,
    unitList = _ref2.unitList,
    isLast = _ref2.isLast,
    onPick = _ref2.onPick;
  return /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      borderBottom: isLast ? undefined : CELL_BORDER
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      padding: '12px 12px 12px 0',
      wordBreak: 'break-word'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, meter.id)), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      padding: '12px 12px',
      borderLeft: CELL_BORDER,
      wordBreak: 'break-word'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, meter.location)), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1.5,
      padding: '4px 0 4px 12px',
      borderLeft: CELL_BORDER
    }
  }, sharedLabel ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, sharedLabel), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    style: {
      fontSize: 12
    }
  }, SHARED_ROW_MESSAGE)) : /*#__PURE__*/React.createElement(Autocomplete, {
    size: "small",
    options: unitList,
    value: holder,
    getOptionLabel: unit => unit.label,
    isOptionEqualToValue: (option, value) => option.id === value.id,
    onChange: (_e, unit) => onPick(meter, unit),
    renderInput: params => /*#__PURE__*/React.createElement(TextField, _extends({}, params, {
      placeholder: "Select Pro unit",
      inputProps: _objectSpread(_objectSpread({}, params.inputProps), {}, {
        'aria-label': "Pro unit for ".concat(meter.id)
      })
    }))
  })));
});

// Defined at module level — never remounts due to parent re-renders
const ProvideUnitMeterIDsFormContent = _ref3 => {
  let dataRef = _ref3.dataRef,
    unitList = _ref3.unitList,
    initialMeterIds = _ref3.initialMeterIds,
    initialPropertyMeterId = _ref3.initialPropertyMeterId,
    activeMapping = _ref3.activeMapping,
    templateState = _ref3.templateState,
    onSameUnitDuplicateChange = _ref3.onSameUnitDuplicateChange;
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const isUploadHeld = templateState !== 'ready';
  const _useState = useState(initialPropertyMeterId || ''),
    _useState2 = _slicedToArray(_useState, 2),
    propertyMeterId = _useState2[0],
    setPropertyMeterId = _useState2[1];
  const _useState3 = useState(() => {
      const init = {};
      unitList.forEach(unit => {
        const vals = initialMeterIds === null || initialMeterIds === void 0 ? void 0 : initialMeterIds[unit.id];
        init[unit.id] = Array.isArray(vals) && vals.length ? vals : [''];
      });
      return init;
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    unitMeterIds = _useState4[0],
    setUnitMeterIds = _useState4[1];
  const _useState5 = useState(() => Object.values(initialMeterIds || {}).some(ids => Array.isArray(ids) && ids.some(Boolean))),
    _useState6 = _slicedToArray(_useState5, 1),
    hasSavedMeterIds = _useState6[0];
  const _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    csvFile = _useState8[0],
    setCsvFile = _useState8[1];
  const _useState9 = useState([]),
    _useState0 = _slicedToArray(_useState9, 2),
    csvHeaders = _useState0[0],
    setCsvHeaders = _useState0[1];
  const _useState1 = useState([]),
    _useState10 = _slicedToArray(_useState1, 2),
    csvRows = _useState10[0],
    setCsvRows = _useState10[1];
  const _useState11 = useState(''),
    _useState12 = _slicedToArray(_useState11, 2),
    meterIdColumn = _useState12[0],
    setMeterIdColumn = _useState12[1];
  const _useState13 = useState(''),
    _useState14 = _slicedToArray(_useState13, 2),
    locationColumn = _useState14[0],
    setLocationColumn = _useState14[1];
  const _useState15 = useState('row'),
    _useState16 = _slicedToArray(_useState15, 2),
    view = _useState16[0],
    setView = _useState16[1];
  const _useState17 = useState(false),
    _useState18 = _slicedToArray(_useState17, 2),
    isDragging = _useState18[0],
    setIsDragging = _useState18[1];
  const _useState19 = useState({}),
    _useState20 = _slicedToArray(_useState19, 2),
    manualInputs = _useState20[0],
    setManualInputs = _useState20[1];
  const fileInputRef = useRef(null);
  const activeFile = useRef(null);
  const eligibleUnitIds = useRef(null);
  // Meter IDs the last pre-fill put on units (unit id -> keys). Only these are taken back on a column change
  // in Edit. A unit the PM changes by hand, in either view, is dropped from it and also stops being filled
  // by a later pre-fill, so hand-picked units win.
  const placedByPrefill = useRef({});
  const touchedUnitIds = useRef(new Set());
  const latestUnitMeterIds = useRef(unitMeterIds);
  latestUnitMeterIds.current = unitMeterIds;
  if (dataRef) {
    dataRef.current = {
      unitMeterIds,
      propertyMeterId
    };
  }
  const sheet = useMemo(() => readSheet(csvRows, csvHeaders, meterIdColumn, locationColumn), [csvRows, csvHeaders, meterIdColumn, locationColumn]);
  const csvMeterOptions = sheet.options;
  const isRowViewAvailable = !!meterIdColumn && !!locationColumn;
  const activeView = isRowViewAvailable ? view : 'unit';

  // Stable handlers, never recreated, use functional setState
  const forgetPlacements = useCallback(unitIds => {
    unitIds.forEach(unitId => {
      placedByPrefill.current = forgetUnitPlacements(placedByPrefill.current, unitId);
    });
  }, []);
  const keepOutOfPrefill = useCallback(unitIds => {
    unitIds.forEach(unitId => touchedUnitIds.current.add(String(unitId)));
  }, []);
  const touchUnits = useCallback(unitIds => {
    forgetPlacements(unitIds);
    keepOutOfPrefill(unitIds);
  }, [forgetPlacements, keepOutOfPrefill]);
  const addMeterId = useCallback(unitId => {
    touchUnits([unitId]);
    setUnitMeterIds(prev => _objectSpread(_objectSpread({}, prev), {}, {
      [unitId]: [...(prev[unitId] || ['']), '']
    }));
  }, [touchUnits]);
  const removeMeterId = useCallback((unitId, index) => {
    touchUnits([unitId]);
    setUnitMeterIds(prev => {
      const arr = [...(prev[unitId] || [''])];
      arr.splice(index, 1);
      return _objectSpread(_objectSpread({}, prev), {}, {
        [unitId]: arr.length ? arr : ['']
      });
    });
  }, [touchUnits]);
  const updateMeterId = useCallback((unitId, index, value) => {
    touchUnits([unitId]);
    setUnitMeterIds(prev => {
      const arr = [...(prev[unitId] || [''])];
      arr[index] = value;
      return _objectSpread(_objectSpread({}, prev), {}, {
        [unitId]: arr
      });
    });
  }, [touchUnits]);
  const toggleManualInput = useCallback((unitId, index) => {
    const key = "".concat(unitId, "_").concat(index);
    touchUnits([unitId]);
    setManualInputs(prev => _objectSpread(_objectSpread({}, prev), {}, {
      [key]: true
    }));
    setUnitMeterIds(prev => {
      const arr = [...(prev[unitId] || [''])];
      arr[index] = '';
      return _objectSpread(_objectSpread({}, prev), {}, {
        [unitId]: arr
      });
    });
  }, [touchUnits]);

  // A move or a clear changes the unit the meter leaves as well as the one it lands on.
  const pickUnit = useCallback((meter, unit) => {
    const leaving = Object.entries(latestUnitMeterIds.current).filter(_ref4 => {
      let _ref5 = _slicedToArray(_ref4, 2),
        ids = _ref5[1];
      return ids.some(id => normalizeMeterId(id) === meter.key);
    }).map(_ref6 => {
      let _ref7 = _slicedToArray(_ref6, 1),
        unitId = _ref7[0];
      return unitId;
    });
    touchUnits(unit ? [...leaving, unit.id] : leaving);
    setUnitMeterIds(prev => moveMeter(prev, meter, unit ? unit.id : null));
  }, [touchUnits]);
  const buildEmptyIds = useCallback(() => {
    const reset = {};
    unitList.forEach(unit => {
      reset[unit.id] = [''];
    });
    return reset;
  }, [unitList]);
  const applyColumns = useCallback((headers, rows, meterColumn, locColumn) => {
    const _readSheet = readSheet(rows, headers, meterColumn, locColumn),
      meters = _readSheet.meters;
    setMeterIdColumn(meterColumn);
    setLocationColumn(locColumn);
    if (!hasSavedMeterIds) setManualInputs({});
    const start = hasSavedMeterIds ? clearPlaced(latestUnitMeterIds.current, placedByPrefill.current) : buildEmptyIds();
    const eligible = eligibleUnitIds.current ? new Set([...eligibleUnitIds.current].filter(unitId => !touchedUnitIds.current.has(unitId))) : null;
    const next = prefillMeterIds({
      meters,
      unitList,
      unitMeterIds: start,
      unitMappings: activeMapping === null || activeMapping === void 0 ? void 0 : activeMapping.unitMappings,
      eligibleUnitIds: eligible
    });
    placedByPrefill.current = findPlaced(start, next);
    latestUnitMeterIds.current = next;
    setUnitMeterIds(next);
  }, [activeMapping, buildEmptyIds, hasSavedMeterIds, unitList]);
  const handleFileSelect = useCallback(async file => {
    var _activeMapping$column, _activeMapping$column2;
    if (isUploadHeld) return;
    activeFile.current = file;
    placedByPrefill.current = {};
    touchedUnitIds.current = new Set();
    const eligibleAtUpload = hasSavedMeterIds ? findEmptyUnitIds(unitList, latestUnitMeterIds.current) : null;
    let parsed;
    try {
      parsed = parseCsvText(await file.text());
    } catch (_unused) {
      parsed = {
        headers: [],
        rows: []
      };
    }
    if (activeFile.current !== file) return;
    const _parsed = parsed,
      headers = _parsed.headers,
      rows = _parsed.rows;
    eligibleUnitIds.current = eligibleAtUpload;
    setCsvFile(file);
    setCsvHeaders(headers);
    setCsvRows(rows);
    setView('row');
    const templateMeter = findHeader(headers, activeMapping === null || activeMapping === void 0 || (_activeMapping$column = activeMapping.columnMappings) === null || _activeMapping$column === void 0 ? void 0 : _activeMapping$column.meterIdColumn);
    const templateLocation = findHeader(headers, activeMapping === null || activeMapping === void 0 || (_activeMapping$column2 = activeMapping.columnMappings) === null || _activeMapping$column2 === void 0 ? void 0 : _activeMapping$column2.unitNameColumn);
    const useTemplate = !!templateMeter && !!templateLocation;
    applyColumns(headers, rows, useTemplate ? templateMeter : '', useTemplate ? templateLocation : '');
  }, [activeMapping, applyColumns, hasSavedMeterIds, isUploadHeld, unitList]);
  const handleFileInputChange = useCallback(async e => {
    var _e$target$files;
    const file = (_e$target$files = e.target.files) === null || _e$target$files === void 0 ? void 0 : _e$target$files[0];
    if (file) await handleFileSelect(file);
  }, [handleFileSelect]);
  const handleDragOver = useCallback(e => {
    e.preventDefault();
    if (!isUploadHeld) setIsDragging(true);
  }, [isUploadHeld]);
  const handleDragLeave = useCallback(() => setIsDragging(false), []);
  const handleDrop = useCallback(async e => {
    var _e$dataTransfer$files;
    e.preventDefault();
    setIsDragging(false);
    const file = (_e$dataTransfer$files = e.dataTransfer.files) === null || _e$dataTransfer$files === void 0 ? void 0 : _e$dataTransfer$files[0];
    if (file) await handleFileSelect(file);
  }, [handleFileSelect]);
  const handleRemoveFile = useCallback(() => {
    activeFile.current = null;
    eligibleUnitIds.current = null;
    placedByPrefill.current = {};
    touchedUnitIds.current = new Set();
    setCsvFile(null);
    setCsvHeaders([]);
    setCsvRows([]);
    setMeterIdColumn('');
    setLocationColumn('');
    if (!hasSavedMeterIds) {
      setManualInputs({});
      setUnitMeterIds(buildEmptyIds());
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [buildEmptyIds, hasSavedMeterIds]);
  const handleMeterColumnChange = column => applyColumns(csvHeaders, csvRows, column, locationColumn);
  const handleLocationColumnChange = column => applyColumns(csvHeaders, csvRows, meterIdColumn, column);

  // Per-slot options: hide only IDs held by another slot of the same unit, so a shared ID stays pickable
  const filteredOptionsMap = useMemo(() => {
    if (!csvMeterOptions.length || activeView !== 'unit') return {};
    const locationByKey = new Map(sheet.meters.map(meter => [meter.key, meter.location]));
    const newMap = {};
    unitList.forEach(unit => {
      const ids = unitMeterIds[unit.id] || [''];
      newMap[unit.id] = ids.map((slotVal, slot) => {
        const taken = new Set(ids.filter((_id, other) => other !== slot).map(normalizeMeterId));
        const offered = csvMeterOptions.filter(opt => opt.value === slotVal || !taken.has(normalizeMeterId(opt.value)));
        const isListed = !slotVal || offered.some(opt => opt.value === slotVal);
        if (isListed) return offered;
        const location = locationByKey.get(normalizeMeterId(slotVal)) || '';
        const label = location ? "".concat(slotVal, " (").concat(location, ")") : slotVal;
        return sortByLocation([...offered, {
          value: slotVal,
          id: slotVal,
          label,
          location
        }]);
      });
    });
    return newMap;
  }, [unitMeterIds, unitList, csvMeterOptions, sheet.meters, activeView]);
  const manualFlagsMap = useMemo(() => {
    const map = {};
    unitList.forEach(unit => {
      const ids = unitMeterIds[unit.id] || [''];
      map[unit.id] = ids.map((_, i) => !!manualInputs["".concat(unit.id, "_").concat(i)]);
    });
    return map;
  }, [unitList, unitMeterIds, manualInputs]);
  const meterIdIssues = useMemo(() => findMeterIdIssues(unitList, unitMeterIds), [unitList, unitMeterIds]);
  const holders = useMemo(() => findHolders(unitList, unitMeterIds), [unitList, unitMeterIds]);
  const hasSameUnitDuplicate = Object.values(meterIdIssues).some(_ref8 => {
    let duplicateSlots = _ref8.duplicateSlots;
    return duplicateSlots.some(Boolean);
  });
  useEffect(() => {
    onSameUnitDuplicateChange === null || onSameUnitDuplicateChange === void 0 || onSameUnitDuplicateChange(hasSameUnitDuplicate);
  }, [hasSameUnitDuplicate, onSameUnitDuplicateChange]);
  const metersWithUnit = sheet.meters.filter(meter => holders.has(meter.key)).length;
  const unitsWithMeter = unitList.filter(unit => (unitMeterIds[unit.id] || []).some(id => id.trim())).length;
  const renderColumnSelect = (value, onChange, placeholder, taken, isRequired) => /*#__PURE__*/React.createElement(FormControl, {
    size: "small",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Select, {
    displayEmpty: true,
    value: value,
    onChange: e => onChange(e.target.value),
    renderValue: val => val ? val : /*#__PURE__*/React.createElement("span", {
      style: {
        color: '#9e9e9e'
      }
    }, placeholder)
  }, !isRequired && /*#__PURE__*/React.createElement(MenuItem, {
    value: ""
  }, "None"), [...new Set(csvHeaders.filter(h => h && h !== taken && csvHeaders.filter(other => other === h).length === 1))].map(h => /*#__PURE__*/React.createElement(MenuItem, {
    key: h,
    value: h
  }, h))));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 16,
      marginBottom: 24
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600,
      width: LABEL_WIDTH + 1,
      flexShrink: 0,
      borderRight: CELL_BORDER,
      padding: '8px 0'
    }
  }, "Master meter ID (optional)"), /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    fullWidth: true,
    placeholder: "123456789",
    value: propertyMeterId,
    onChange: e => setPropertyMeterId(e.target.value),
    helperText: "The property's main meter, if it has one. Leave empty if not."
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      border: "1px dashed ".concat(isDragging ? '#1976d2' : '#b8c4ce'),
      borderRadius: 4,
      padding: csvFile ? 12 : 32,
      cursor: !csvFile && !isUploadHeld ? 'pointer' : 'default',
      marginBottom: 16,
      backgroundColor: isDragging ? 'rgba(25,118,210,0.04)' : undefined
    },
    onClick: () => {
      var _fileInputRef$current;
      return !csvFile && !isUploadHeld && ((_fileInputRef$current = fileInputRef.current) === null || _fileInputRef$current === void 0 ? void 0 : _fileInputRef$current.click());
    },
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop
  }, !csvFile ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    align: "center",
    color: "light"
  }, "Upload a CSV to auto-fill meter IDs (optional)") : /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: 12
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "file-text"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontWeight: 600
    }
  }, csvFile.name), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    size: "small",
    style: {
      minWidth: 0
    },
    onClick: e => {
      e.stopPropagation();
      handleRemoveFile();
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "trash"
  }))), /*#__PURE__*/React.createElement("input", {
    ref: fileInputRef,
    type: "file",
    accept: ".csv",
    hidden: true,
    disabled: isUploadHeld,
    onChange: handleFileInputChange
  })), templateState === 'failed' && /*#__PURE__*/React.createElement(FormHelperText, {
    error: true,
    role: "alert",
    style: {
      marginTop: 0,
      marginBottom: 16
    }
  }, t('general.meter_ids_template_failed')), csvHeaders.some(Boolean) && /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      gap: 16,
      marginBottom: 16
    }
  }, renderColumnSelect(meterIdColumn, handleMeterColumnChange, 'Select meter ID column', locationColumn, true), renderColumnSelect(locationColumn, handleLocationColumnChange, 'Select location column', meterIdColumn, false)), isRowViewAvailable && /*#__PURE__*/React.createElement(Tabs, {
    value: activeView,
    onChange: (_e, next) => setView(next),
    style: {
      marginBottom: 8
    }
  }, /*#__PURE__*/React.createElement(Tab, {
    value: "row",
    label: "By sheet row"
  }), /*#__PURE__*/React.createElement(Tab, {
    value: "unit",
    label: "By unit"
  })), activeView === 'row' ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    style: {
      margin: '8px 0'
    }
  }, "".concat(metersWithUnit, " of ").concat(sheet.meters.length, " meters in the file have a Pro unit.")), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      borderBottom: CELL_BORDER
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      padding: '12px 12px 12px 0'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600
    }
  }, "Meter ID")), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      padding: '12px 12px',
      borderLeft: CELL_BORDER
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600
    }
  }, "Location")), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1.5,
      padding: '12px 0 12px 12px',
      borderLeft: CELL_BORDER
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600
    }
  }, "Pro unit"))), sheet.meters.map((meter, idx) => {
    const held = holders.get(meter.key) || [];
    return /*#__PURE__*/React.createElement(SheetMeterRow, {
      key: meter.key,
      meter: meter,
      holder: held.length === 1 ? held[0] : null,
      sharedLabel: held.length > 1 ? held.map(unit => unit.label).join(', ') : '',
      unitList: unitList,
      isLast: idx === sheet.meters.length - 1,
      onPick: pickUnit
    });
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    style: {
      margin: '8px 0'
    }
  }, "".concat(unitsWithMeter, " of ").concat(unitList.length, " units have a meter ID.")), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      borderBottom: CELL_BORDER
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: LABEL_WIDTH,
      flexShrink: 0,
      padding: '12px 12px 12px 0'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600
    }
  }, "Unit")), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      padding: '4px 0 4px 12px',
      borderLeft: CELL_BORDER
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600
    }
  }, "Meter ID"))), unitList.map((unit, idx) => {
    var _filteredOptionsMap$u;
    return /*#__PURE__*/React.createElement(UnitMeterIdRow, {
      key: unit.id,
      unit: unit,
      idx: idx,
      unitCount: unitList.length,
      ids: unitMeterIds[unit.id] || [''],
      csvMeterOptions: csvMeterOptions,
      filteredOptions: (_filteredOptionsMap$u = filteredOptionsMap[unit.id]) !== null && _filteredOptionsMap$u !== void 0 ? _filteredOptionsMap$u : null,
      manualFlags: manualFlagsMap[unit.id] || [],
      issues: meterIdIssues[unit.id] || NO_ISSUES,
      onAdd: addMeterId,
      onRemove: removeMeterId,
      onUpdate: updateMeterId,
      onToggleManual: toggleManualInput
    });
  })));
};
const ProvideUnitMeterIDsModalBase = _ref9 => {
  var _store$homeDashboardS, _store$homeDashboardS2, _utility$provider;
  let utility = _ref9.utility,
    propertyUtilityId = _ref9.propertyUtilityId,
    hasMeterIds = _ref9.hasMeterIds,
    activeMapping = _ref9.activeMapping,
    _ref9$templateState = _ref9.templateState,
    templateState = _ref9$templateState === void 0 ? 'ready' : _ref9$templateState,
    onSave = _ref9.onSave,
    onHasMeterIdsChange = _ref9.onHasMeterIdsChange;
  const store = useStore();
  const _useState21 = useState(false),
    _useState22 = _slicedToArray(_useState21, 2),
    isOpen = _useState22[0],
    setIsOpen = _useState22[1];
  const _useState23 = useState(0),
    _useState24 = _slicedToArray(_useState23, 2),
    formKey = _useState24[0],
    setFormKey = _useState24[1];
  const _useState25 = useState({}),
    _useState26 = _slicedToArray(_useState25, 2),
    initialMeterIds = _useState26[0],
    setInitialMeterIds = _useState26[1];
  const _useState27 = useState(''),
    _useState28 = _slicedToArray(_useState27, 2),
    initialPropertyMeterId = _useState28[0],
    setInitialPropertyMeterId = _useState28[1];
  const _useState29 = useState(false),
    _useState30 = _slicedToArray(_useState29, 2),
    deleteConfirmOpen = _useState30[0],
    setDeleteConfirmOpen = _useState30[1];
  const _useState31 = useState(false),
    _useState32 = _slicedToArray(_useState31, 2),
    hasSameUnitDuplicate = _useState32[0],
    setHasSameUnitDuplicate = _useState32[1];
  // The saved IDs are read when the dialog opens. If that read fails the dialog is read only, like a failed
  // load read, so Save cannot send an empty list over the saved IDs.
  const _useState33 = useState(false),
    _useState34 = _slicedToArray(_useState33, 2),
    openReadFailed = _useState34[0],
    setOpenReadFailed = _useState34[1];
  const dataRef = useRef({});
  const saveInFlight = useRef(false);
  const deleteInFlight = useRef(false);
  const storeUnits = (_store$homeDashboardS = (_store$homeDashboardS2 = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS2 === void 0 || (_store$homeDashboardS2 = _store$homeDashboardS2.selectedPropertyDetails()) === null || _store$homeDashboardS2 === void 0 || (_store$homeDashboardS2 = _store$homeDashboardS2.details) === null || _store$homeDashboardS2 === void 0 || (_store$homeDashboardS2 = _store$homeDashboardS2.unitSetup) === null || _store$homeDashboardS2 === void 0 ? void 0 : _store$homeDashboardS2.units) !== null && _store$homeDashboardS !== void 0 ? _store$homeDashboardS : [];
  const unitList = storeUnits.filter(u => u.id && u.name).sort((a, b) => a.name.localeCompare(b.name, undefined, {
    numeric: true
  })).map(u => ({
    id: u.id,
    label: u.name
  }));
  const subtitle = [utility === null || utility === void 0 ? void 0 : utility.utilityType, utility === null || utility === void 0 || (_utility$provider = utility.provider) === null || _utility$provider === void 0 ? void 0 : _utility$provider.utilityProviderName].filter(Boolean).join(', ');

  // The trigger stays off until the mapping reads and the saved ID count have answered, so the dialog opens on
  // the active mapping, in the right Provide or Edit mode, and read only if a read failed.
  // Provide, Edit and Delete are held while a delete is in flight too: an editor opened meanwhile could show
  // the IDs being deleted and send them back on Save.
  const _useState35 = useState(false),
    _useState36 = _slicedToArray(_useState35, 2),
    isDeleting = _useState36[0],
    setIsDeleting = _useState36[1];
  const isTriggerHeld = templateState === 'loading' || isDeleting;
  const isReadOnly = templateState === 'failed' || openReadFailed;

  // One open per click: the button stops the click from reaching the modal wrapper, which would open a second
  // time. Only the latest open's read is applied, so an earlier read that a newer open cancelled (it comes back
  // undefined) is dropped. For the latest open, undefined with the action's error set is a failed read (a thrown
  // HTTP error returns undefined too) and opens the dialog read only; undefined with no error is a cancel by
  // another run of the same action and is dropped.
  const openSeq = useRef(0);
  const handleOpen = async () => {
    if (isTriggerHeld) return;
    const seq = ++openSeq.current;
    let meterIds = {};
    let propertyMeterId = '';
    let didRead = true;
    if (propertyUtilityId) {
      const resp = await store.allocations.getSubmeterBillingUnitMeterMapsAsync.run({
        propertyUtilityId
      });
      if (seq !== openSeq.current) return;
      // See openSeq above: undefined without the action's error is a cancel, with it a failed read.
      if (resp === undefined && !store.allocations.getSubmeterBillingUnitMeterMapsAsync.error) return;
      didRead = !!(resp !== null && resp !== void 0 && resp.isSuccessful && Array.isArray(resp.response));
      if (didRead) {
        resp.response.forEach(entry => {
          if (!propertyMeterId && (entry.propertyMeterId || entry.propertyUtilityMeterId)) {
            propertyMeterId = entry.propertyMeterId || entry.propertyUtilityMeterId;
          }
          if (entry.unitId && entry.meterId) {
            if (!meterIds[entry.unitId]) meterIds[entry.unitId] = [];
            meterIds[entry.unitId].push(entry.meterId);
          }
        });
      }
    }
    setOpenReadFailed(!didRead);
    setInitialMeterIds(meterIds);
    setInitialPropertyMeterId(propertyMeterId);
    setFormKey(k => k + 1);
    setIsOpen(true);
  };
  const openFromButton = e => {
    e.stopPropagation();
    handleOpen();
  };
  const handleClose = () => setIsOpen(false);
  const runSave = async () => {
    const _dataRef$current = dataRef.current,
      unitMeterIds = _dataRef$current.unitMeterIds,
      propertyMeterId = _dataRef$current.propertyMeterId;
    let uniqueUnitCount = 0;
    if (propertyUtilityId) {
      var _store$homeDashboardS3;
      const propertyId = (_store$homeDashboardS3 = store.homeDashboardStore.getCurrentProperty) === null || _store$homeDashboardS3 === void 0 ? void 0 : _store$homeDashboardS3.id;
      const maps = [];
      unitList.forEach(unit => {
        ((unitMeterIds === null || unitMeterIds === void 0 ? void 0 : unitMeterIds[unit.id]) || []).filter(Boolean).forEach(meterId => {
          maps.push({
            propertyId,
            propertyUtilityId,
            propertyMeterId: propertyMeterId || '',
            unitId: unit.id,
            meterId
          });
        });
      });
      uniqueUnitCount = new Set(maps.map(m => m.unitId)).size;
      if (hasMeterIds) {
        const resp = await store.allocations.postSubmeterBillingUnitMeterMapsByPropertyUtilityAsync.run({
          propertyUtilityId,
          maps
        });
        if (toastIfRefused(resp, SAVE_REFUSED_MESSAGE)) return;
        toast('success', {
          body: ['Meter IDs updated!']
        });
      } else {
        const resp = await store.allocations.postSubmeterBillingUnitMeterMapsBatchAsync.run({
          maps
        });
        if (toastIfRefused(resp, SAVE_REFUSED_MESSAGE)) return;
        toast('success', {
          body: ['Meter IDs saved!']
        });
      }
    }
    onHasMeterIdsChange === null || onHasMeterIdsChange === void 0 || onHasMeterIdsChange(uniqueUnitCount);
    onSave === null || onSave === void 0 || onSave(dataRef.current);
    handleClose();
  };
  const runDelete = async () => {
    // An Edit read still pending must not open the editor on IDs this delete is removing.
    openSeq.current += 1;
    setIsDeleting(true);
    let resp;
    try {
      resp = await store.allocations.deleteSubmeterBillingUnitMeterMapsByPropertyUtilityAsync.run({
        propertyUtilityId
      });
    } finally {
      setIsDeleting(false);
    }
    if (toastIfRefused(resp, DELETE_REFUSED_MESSAGE)) return;
    toast('success', {
      body: ['Meter IDs deleted!']
    });
    setDeleteConfirmOpen(false);
    setIsOpen(false);
    onHasMeterIdsChange === null || onHasMeterIdsChange === void 0 || onHasMeterIdsChange(0);
  };
  const guarded = (flag, run) => async () => {
    if (flag.current) return;
    flag.current = true;
    try {
      await run();
    } finally {
      flag.current = false;
    }
  };
  const handleSave = guarded(saveInFlight, runSave);
  const handleDeleteConfirm = guarded(deleteInFlight, runDelete);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SimpleModal, {
    trigger: hasMeterIds ? /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        alignItems: 'center'
      }
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "actionButton",
      onClick: openFromButton,
      disabled: isTriggerHeld
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "edit"
    }), /*#__PURE__*/React.createElement("span", {
      className: "actionText"
    }, "Edit")), /*#__PURE__*/React.createElement(Button, {
      variant: "actionButton",
      disabled: isTriggerHeld
      // The modal wrapper opens the editor on any key; Delete keeps its own keyboard activation.
      ,
      onKeyDown: e => e.stopPropagation(),
      onClick: e => {
        e.stopPropagation();
        setDeleteConfirmOpen(true);
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "trash"
    }), /*#__PURE__*/React.createElement("span", {
      className: "actionText"
    }, "Delete"))) : /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      size: "small",
      onClick: openFromButton,
      disabled: isTriggerHeld
    }, "Provide"),
    title: /*#__PURE__*/React.createElement(Typography, {
      variant: "h4"
    }, "Provide unit meter IDs"),
    subTitle: subtitle ? /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "light"
    }, subtitle) : undefined,
    rightButton: /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      onClick: handleSave,
      disabled: isReadOnly || hasSameUnitDuplicate || store.allocations.postSubmeterBillingUnitMeterMapsBatchAsync.inProgress || store.allocations.postSubmeterBillingUnitMeterMapsByPropertyUtilityAsync.inProgress
    }, "Save meter IDs"),
    isOpen: isOpen,
    disabledTrigger: isTriggerHeld,
    handleOpen: handleOpen,
    handleClose: handleClose
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    style: {
      marginBottom: 16
    }
  }, "Changed this after reads were saved? Recalculate on Meter details to remove charges from units that are no longer mapped. Newly mapped units won't be added to a cycle that already has saved rows."), /*#__PURE__*/React.createElement(ProvideUnitMeterIDsFormContent, {
    key: formKey,
    dataRef: dataRef,
    unitList: unitList,
    initialMeterIds: initialMeterIds,
    initialPropertyMeterId: initialPropertyMeterId,
    activeMapping: activeMapping,
    templateState: isReadOnly ? 'failed' : templateState,
    onSameUnitDuplicateChange: setHasSameUnitDuplicate
  })), /*#__PURE__*/React.createElement(Dialog, {
    open: deleteConfirmOpen,
    onClose: () => setDeleteConfirmOpen(false)
  }, /*#__PURE__*/React.createElement(DialogTitle, null, "Delete meter ID configuration"), /*#__PURE__*/React.createElement(DialogContent, null, /*#__PURE__*/React.createElement(DialogContentText, null, "Are you sure you want to delete all meter ID mappings for this utility? This action cannot be undone.")), /*#__PURE__*/React.createElement(DialogActions, null, /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    onClick: () => setDeleteConfirmOpen(false)
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    onClick: handleDeleteConfirm,
    disabled: store.allocations.deleteSubmeterBillingUnitMeterMapsByPropertyUtilityAsync.inProgress
  }, "Delete"))));
};
export const ProvideUnitMeterIDsModal = observer(ProvideUnitMeterIDsModalBase);